import React from 'react';
import styled from '@emotion/styled';
import { useTheme } from 'emotion-theming';
import PropTypes from 'prop-types';
import IcomoonReact from 'icomoon-react';
import iconSet from '../../../../shared/images/teambuildr-selection.json';
import { focusRing } from '../../../../shared/theme/focusStyles';

/** The status pill shown in the transposed table header for each enabled
 * exercise option. Replaces the copy-pasted `StateBadge` each table used to
 * declare for itself. Pass `onRemove` to render the x that toggles the option
 * back off - options the coach can't change (locked or greyed out) get no x. */
/** The pill and the Options trigger sit side by side and must both come out
 * 28px tall. Neither uses a fixed height - 6px + 16px + 6px gets there from
 * padding, so the control still grows if its text ever wraps. Pinning the line
 * height is what keeps the two matched: without it each font size would set its
 * own line box and they would drift apart. 14px radius is half the height,
 * which is what makes the ends fully round. */
export const OPTION_CONTROL_VERTICAL_PADDING = '6px';
export const OPTION_CONTROL_LINE_HEIGHT = '16px';

/** Type matches the Options trigger so the two read as one control family. */
export const OPTION_CONTROL_FONT_SIZE = '12px';
export const OPTION_CONTROL_FONT_WEIGHT = 600;

const Pill = styled('div')`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  gap: 6px;
  /** The border's 1px comes out of the padding, so the pill stays the same
   * height as the Options trigger beside it. */
  padding: calc(${OPTION_CONTROL_VERTICAL_PADDING} - 1px) 9px;
  font-family: 'Nunito Sans';
  font-size: ${OPTION_CONTROL_FONT_SIZE};
  line-height: ${OPTION_CONTROL_LINE_HEIGHT};
  font-weight: ${OPTION_CONTROL_FONT_WEIGHT};
  color: ${({ theme }) => theme.colors.text};
  /** One step above the row in dark mode, and clearly apart from the
   * buttonPrimaryBg Options trigger beside it. */
  background: ${({ theme }) => theme.colors.surfaceTertiary};
  /** Build's hairline in light. In dark that hairline (#333) matches the pill
   * fill, so it steps up to borderStrong to stay visible. */
  border: 1px solid ${({ theme }) => (theme.isDark ? theme.colors.borderStrong : theme.colors.borderMedium)};
  border-radius: 14px;
  white-space: nowrap;
`;

/** The press handlers stop propagation because the pill sits inside the
 * react-beautiful-dnd drag handle - a press that reaches the handle starts a
 * phantom drag (the same failure StatusBadgeScroller's arrows guard against). */
const RemoveButton = styled('button')`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 14px;
  height: 14px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  cursor: pointer;
  /** The pill's own border ink: one clear step from the pill fill in both
   * modes (darker in light, lighter in dark). surfaceHover was too faint on the
   * tertiary fill, and surfaceHoverStrong matches that fill. */
  &:hover {
    background: ${({ theme }) => (theme.isDark ? theme.colors.borderStrong : theme.colors.borderMedium)};
  }
  ${focusRing}
`;

const ExerciseOptionPill = ({ label, onRemove, optionId }) => {
  const { colors } = useTheme();

  return (
    <Pill>
      {label}
      {onRemove && (
        <RemoveButton
          type='button'
          aria-label={`Remove ${label}`}
          /** How ExerciseOptionsBar finds the next x after this pill unmounts, and
           * how it tells whether this button was the one holding focus. */
          data-option-id={optionId}
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
        >
          {/** Lighter than the label so the x reads as secondary to it. */}
          <IcomoonReact iconSet={iconSet} size={7} icon='remove' color={colors.textSecondary} />
        </RemoveButton>
      )}
    </Pill>
  );
};

ExerciseOptionPill.propTypes = {
  label: PropTypes.string.isRequired,
  onRemove: PropTypes.func,
  optionId: PropTypes.string,
};

ExerciseOptionPill.defaultProps = {
  onRemove: null,
  optionId: undefined,
};

export default ExerciseOptionPill;
